/**
 * §v3.glass-popover (2026-05-07) — the canonical popover primitive.
 *
 * WHY THIS COMPONENT EXISTS
 * ─────────────────────────
 * The original ``.tk-chip-menu`` div was rendered as a child of the
 * trigger button inside the ticket modal. Two things conspired to
 * break it on Windows Chrome (working perfectly on macOS Chrome):
 *
 *   1. The Ticket modal uses ``transform: scale(.97)`` for its open
 *      animation. ANY transformed ancestor strips ``backdrop-filter``
 *      from descendants on Chromium-on-Windows (the Mac compositor
 *      handles the same case via implicit layer promotion; Windows
 *      doesn't). Symptom: the dropdown's translucent fill renders
 *      as flat opaque grey-purple instead of the liquid-glass blur.
 *
 *   2. Some Windows Chrome installs run with GPU acceleration
 *      disabled (corporate-managed laptops, old Intel drivers). In
 *      that mode ``backdrop-filter`` silently no-ops — the
 *      translucent rgba background becomes the only thing visible
 *      and the canvas blur underneath is gone.
 *
 * This component closes BOTH gaps structurally so future popovers
 * can never re-introduce the bug:
 *
 *   • Renders into a ``<body>``-level React portal — no transformed
 *     ancestor can reach it.
 *   • Uses ``position: fixed`` from the anchor's bounding rect, so
 *     it positions correctly regardless of scroll/transform context.
 *   • Forces a fresh stacking context (``isolation: isolate`` +
 *     ``transform: translateZ(0)``) so Chromium's Windows compositor
 *     allocates a real GPU layer for the popover.
 *   • The shared ``glass-popover.css`` wraps the glass treatment in
 *     ``@supports (backdrop-filter)`` so machines without GPU comp
 *     fall back to a slightly-darker but legible opaque tint instead
 *     of the half-broken see-through ghost they'd otherwise see.
 *   • Repositions on scroll + resize so a long page scroll doesn't
 *     leave the popover stranded.
 *   • Built-in click-outside (anchor-aware: clicking the trigger to
 *     close it is recognised) + ESC handling.
 *
 * USAGE
 * ─────
 *   const triggerRef = useRef<HTMLButtonElement | null>(null);
 *   const [open, setOpen] = useState(false);
 *
 *   <button ref={triggerRef} onClick={() => setOpen(o => !o)} />
 *   <GlassPopover
 *     open={open}
 *     anchor={triggerRef}
 *     onClose={() => setOpen(false)}
 *     align="end"
 *     ariaLabel="Status"
 *   >
 *     <button>Option A</button>
 *     <button>Option B</button>
 *   </GlassPopover>
 *
 * STRUCTURAL CONTRACT
 * ───────────────────
 * Every dropdown / context menu / floating popover MUST go through
 * this component. The glass styling is defined ONLY in
 * ``glass-popover.css`` — do NOT replicate the ``backdrop-filter``
 * recipe inline elsewhere or you'll re-introduce the Windows bug.
 *
 * If you find yourself writing ``className="..." backdrop-filter:..."
 * for a floating menu — STOP and use this component instead.
 */

import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./glass-popover.css";

export type PopoverAlign = "start" | "center" | "end";
export type PopoverSide = "bottom" | "top";

export interface GlassPopoverProps {
  /** Open state — keep this controlled by the parent. */
  open: boolean;
  /** Ref to the trigger element. We measure its bounding rect to
   * position the popover. */
  anchor: RefObject<HTMLElement | null>;
  /** Called when the user clicks outside (anywhere except trigger
   * + popover) or hits Escape. */
  onClose: () => void;
  /** Where to align the popover horizontally relative to the
   * trigger. Defaults to ``end`` (right-aligned with trigger). */
  align?: PopoverAlign;
  /** Open above or below the trigger. Defaults to ``bottom``;
   * automatically flips to ``top`` if the popover would clip the
   * viewport bottom. */
  side?: PopoverSide;
  /** Pixels between trigger and popover. Default 6. */
  offset?: number;
  /** Fixed width if the popover should not size to its content. */
  minWidth?: number;
  /** ARIA listbox label — strongly recommended for screen readers. */
  ariaLabel?: string;
  /** Optional extra class on the popover surface. The component
   * already applies ``glass-popover``. */
  className?: string;
  /** Inline style overrides — use sparingly. The positioning + glass
   * recipe are managed by the component; don't override
   * ``position``, ``top``, ``left``, ``backdropFilter``, etc. */
  style?: CSSProperties;
  children: ReactNode;
}

interface ResolvedPosition {
  top: number;
  left: number;
  /** True when we flipped from bottom→top because of viewport. */
  flipped: boolean;
}

/** Compute fixed coordinates for the popover. */
function computePosition(
  anchorRect: DOMRect,
  popoverWidth: number,
  popoverHeight: number,
  align: PopoverAlign,
  side: PopoverSide,
  offset: number,
): ResolvedPosition {
  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;

  let top =
    side === "bottom"
      ? anchorRect.bottom + offset
      : anchorRect.top - popoverHeight - offset;

  // Auto-flip if the chosen side overflows.
  let flipped = false;
  if (side === "bottom" && top + popoverHeight > viewportH - 8) {
    const flippedTop = anchorRect.top - popoverHeight - offset;
    if (flippedTop > 8) {
      top = flippedTop;
      flipped = true;
    }
  } else if (side === "top" && top < 8) {
    const flippedTop = anchorRect.bottom + offset;
    if (flippedTop + popoverHeight < viewportH - 8) {
      top = flippedTop;
      flipped = true;
    }
  }

  let left: number;
  switch (align) {
    case "start":
      left = anchorRect.left;
      break;
    case "center":
      left = anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
      break;
    case "end":
    default:
      left = anchorRect.right - popoverWidth;
      break;
  }
  // Clamp to viewport with an 8px gutter.
  left = Math.max(8, Math.min(left, viewportW - popoverWidth - 8));

  return { top, left, flipped };
}

export function GlassPopover({
  open,
  anchor,
  onClose,
  align = "end",
  side = "bottom",
  offset = 6,
  minWidth,
  ariaLabel,
  className,
  style,
  children,
}: GlassPopoverProps) {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<ResolvedPosition | null>(null);

  // Stable callback so the effect deps don't churn.
  const reposition = useCallback(() => {
    const anchorEl = anchor.current;
    const popoverEl = popoverRef.current;
    if (!anchorEl || !popoverEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const popoverRect = popoverEl.getBoundingClientRect();
    setPos(
      computePosition(
        rect,
        popoverRect.width || minWidth || 170,
        popoverRect.height || 0,
        align,
        side,
        offset,
      ),
    );
  }, [anchor, align, side, offset, minWidth]);

  // Initial measurement runs synchronously so the popover never
  // flashes at (0, 0) before reposition lands.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    reposition();
  }, [open, reposition]);

  // Reposition on scroll/resize while open.
  useEffect(() => {
    if (!open) return;
    const handler = () => reposition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open, reposition]);

  // Click-outside + ESC.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const popoverEl = popoverRef.current;
      const anchorEl = anchor.current;
      const target = e.target as Node;
      if (popoverEl && popoverEl.contains(target)) return;
      if (anchorEl && anchorEl.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, anchor, onClose]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Render hidden during the first paint so getBoundingClientRect
  // can measure the real popover size; visibility:hidden keeps
  // layout stable, opacity:0 hides the flash.
  const measured = pos !== null;
  const inlineStyle: CSSProperties = {
    position: "fixed",
    top: measured ? pos.top : 0,
    left: measured ? pos.left : 0,
    minWidth: minWidth ?? 170,
    visibility: measured ? "visible" : "hidden",
    opacity: measured ? 1 : 0,
    ...style,
  };

  return createPortal(
    <div
      ref={popoverRef}
      className={
        "glass-popover" + (className ? " " + className : "")
      }
      role="listbox"
      aria-label={ariaLabel}
      style={inlineStyle}
      data-side={side}
      data-flipped={pos?.flipped ? "true" : "false"}
    >
      {children}
    </div>,
    document.body,
  );
}
